import { createContext, useContext, useEffect, type ReactNode } from 'react';
import type { OrganizationListItem } from '../dtos/wire';
import { useSession } from './SessionProvider';

/**
 * Active-organization selection.
 *
 * The organization *list* is server state (`useSession().organizations`);
 * the *selection* is a per-device user preference. We split them
 * deliberately:
 *
 *   - The selection lives in app-owned, platform-specific persisted
 *     state (AsyncStorage on mobile, localStorage on web). The shared
 *     `@repo/api-client` package never imports either backend; it just
 *     accepts a `value` and `onChange` from the host.
 *   - Hooks like `useActiveOrganizationId()` and `useActiveOrganization()`
 *     give every consumer (queries, screens, settings) one canonical
 *     surface, regardless of how the host chose to persist it.
 *
 * This is the integration point between session (server-owned) and
 * persisted preference (device-owned). It is intentionally minimal:
 * a single React context with two values, plus a guard hook that
 * keeps the persisted id from outliving the user's actual access.
 */

interface ActiveOrganizationContextValue {
  activeOrganizationId: string | null;
  setActiveOrganizationId: (id: string | null) => void;
}

const ActiveOrganizationContext =
  createContext<ActiveOrganizationContextValue | null>(null);

export interface ActiveOrganizationProviderProps {
  /**
   * Currently selected organization id, or `null` if none. The host
   * sources this from its platform-specific persisted state (e.g.
   * a `useState` backed by AsyncStorage / localStorage).
   */
  value: string | null;
  /**
   * Setter the host wires to its persistence layer. Called whenever
   * a consumer switches the active organization.
   */
  onChange: (id: string | null) => void;
  children: ReactNode;
}

export function ActiveOrganizationProvider({
  value,
  onChange,
  children,
}: ActiveOrganizationProviderProps) {
  return (
    <ActiveOrganizationContext.Provider
      value={{ activeOrganizationId: value, setActiveOrganizationId: onChange }}
    >
      {children}
    </ActiveOrganizationContext.Provider>
  );
}

function useActiveOrganizationContext(): ActiveOrganizationContextValue {
  const ctx = useContext(ActiveOrganizationContext);
  if (!ctx) {
    throw new Error(
      'useActiveOrganization*() called without an <ActiveOrganizationProvider>. ' +
        'Wrap your app root with one — see docs/04-state-and-structure.md.',
    );
  }
  return ctx;
}

export function useActiveOrganizationId(): string | null {
  return useActiveOrganizationContext().activeOrganizationId;
}

export function useSetActiveOrganizationId(): (id: string | null) => void {
  return useActiveOrganizationContext().setActiveOrganizationId;
}

/**
 * Resolve the active organization *object* by composing the persisted
 * id with the live `session.organizations` list. Returns `null` when
 * unauthenticated, when no org is selected, or when the selection
 * does not (yet) match a known org.
 *
 * Cheap because the orgs list is small and stable across renders.
 */
export function useActiveOrganization(): OrganizationListItem | null {
  const id = useActiveOrganizationId();
  const { organizations } = useSession();
  if (id === null) return null;
  return organizations.find((org) => org.id === id) ?? null;
}

export function useHasActiveOrganization(): boolean {
  return useActiveOrganization() !== null;
}

/**
 * Setter convenience: accepts an `Organization`, an id string, or
 * `null` to clear. Apps mostly call this with the org object as a
 * direct result of a "switch organization" UI action.
 */
export function useSetActiveOrganization() {
  const setId = useSetActiveOrganizationId();
  return (next: OrganizationListItem | { id: string } | string | null) => {
    if (next === null) {
      setId(null);
      return;
    }
    setId(typeof next === 'string' ? next : next.id);
  };
}

/**
 * Keeps the persisted active-organization id consistent with the live
 * organizations list. Mount this once near the app root, inside
 * `<ActiveOrganizationProvider>` and `<SessionProvider>`.
 *
 * Behaviour:
 *   - On `authenticated` AND `organizationsLoaded`: if no id is set,
 *     or the persisted id is no longer in `organizations` (membership
 *     revoked, org deleted), fall back to `organizations[0]` and
 *     write it back.
 *   - On `anonymous`: clear the selection so the next sign-in can't
 *     inherit a stale value from a different account.
 *
 * Why we gate on `organizationsLoaded`:
 *   On dev reload (or after a network blip) the bootstrap can leave
 *   us in `status: 'authenticated'` with `organizations: []` while
 *   the orgs probe is still settling. Without the gate, this guard
 *   would see "id not in []", call `setActiveOrganizationId(null)`,
 *   and `apps/mobile/session/activeOrganizationStorage.ts` would
 *   delete the persisted preference — silently logging the user out
 *   of their last-active workspace just because the network was
 *   slow on reload. The flag distinguishes "we authoritatively know
 *   the user has zero orgs" from "we don't yet know".
 *
 * This is the structural fix for the "stale persisted org outlives
 * real access" bug class — there is no way for the active selection
 * to outlive the user's actual access, but it also can't be wiped
 * by a transient probe failure.
 */
export function useActiveOrganizationGuard(): void {
  const { status, organizations, organizationsLoaded } = useSession();
  const activeOrganizationId = useActiveOrganizationId();
  const setActiveOrganizationId = useSetActiveOrganizationId();

  useEffect(() => {
    if (status === 'authenticated') {
      if (!organizationsLoaded) return;
      const stillAccessible =
        activeOrganizationId !== null &&
        organizations.some((org) => org.id === activeOrganizationId);
      if (!stillAccessible) {
        setActiveOrganizationId(organizations[0]?.id ?? null);
      }
      return;
    }
    if (status === 'anonymous' && activeOrganizationId !== null) {
      setActiveOrganizationId(null);
    }
  }, [
    status,
    organizations,
    organizationsLoaded,
    activeOrganizationId,
    setActiveOrganizationId,
  ]);
}
